import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { WELCOME, FREE_PITCHES, FULL, RECEPTION, CLOSED } from "@/lib/displayLangs";
import { Sun, CloudSun, Cloud, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning, Clock } from "lucide-react";

const weatherIcon = (code) => {
  if (code === 0) return Sun;
  if (code <= 2) return CloudSun;
  if (code === 3) return Cloud;
  if (code <= 49) return CloudFog;
  if (code <= 59) return CloudDrizzle;
  if (code <= 69) return CloudRain;
  if (code <= 79) return CloudSnow;
  if (code <= 84) return CloudRain;
  if (code <= 94) return CloudSnow;
  return CloudLightning;
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ROTATE_MS = 3000;

const theme = (pct) => {
  if (pct > 98) return { bg: "#ef5350", fg: "#ffffff", soft: "rgba(255,255,255,0.78)" };
  if (pct > 90) return { bg: "#f08c2e", fg: "#ffffff", soft: "rgba(255,255,255,0.78)" };
  return { bg: "#2e9e5b", fg: "#ffffff", soft: "rgba(255,255,255,0.78)" };
};

export default function Display() {
  const { tenant } = useParams();
  const [params] = useSearchParams();
  const key = params.get("key") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [i, setI] = useState(0);
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    let closed = false;
    const base = `${BACKEND}/api/public/display/${tenant}`;
    const fetchOnce = () => axios.get(base, { params: { key } })
      .then((r) => { if (!closed) { setData(r.data); setError(""); } })
      .catch((e) => { if (!closed) setError(e.response?.data?.detail || "Scherm niet beschikbaar"); });

    // Eerste cijfers meteen tonen
    fetchOnce();

    // Live push via Server-Sent Events: de server duwt nieuwe cijfers bij elke wijziging.
    let es = null;
    try {
      es = new EventSource(`${base}/stream?key=${encodeURIComponent(key)}`);
      es.onmessage = (ev) => {
        try { const d = JSON.parse(ev.data); if (!closed) { setData(d); setError(""); } } catch (_) { /* keepalive */ }
      };
    } catch (_) { es = null; }

    // Vangnet: als de live-verbinding geblokkeerd wordt, toch elke 60s verversen.
    const poll = setInterval(fetchOnce, 60000);

    return () => { closed = true; if (es) es.close(); clearInterval(poll); };
  }, [tenant, key]);

  useEffect(() => {
    const t = setInterval(() => setI((x) => x + 1), ROTATE_MS);
    return () => clearInterval(t);
  }, []);

  if (error) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-slate-900 p-8 text-center"
        data-testid="display-error">
        <p className="font-display text-3xl font-bold text-slate-200">{error}</p>
      </div>
    );
  }
  if (!data) return <div className="h-screen w-screen bg-slate-900" data-testid="display-loading" />;

  const pct = data.occupancy || 0;
  const c = theme(pct);
  const full = pct > 98;
  const n = WELCOME.length;
  const idx = i % n;

  return (
    <div className="relative flex h-screen w-screen flex-col overflow-hidden select-none"
      style={{ backgroundColor: c.bg, color: c.fg }} data-testid="display-board"
      data-occupancy={pct} data-full={full ? "1" : "0"}>

      <div className="relative flex h-1/3 flex-col items-center justify-center gap-[1vh] border-b-4 px-[3vw] text-center"
        style={{ borderColor: "rgba(255,255,255,0.25)" }}>
        {data.logo_file_id && !logoFailed && (
          <img src={`${BACKEND}/api/public/logo?which=main&v=${data.logo_file_id}&tenant=${tenant}`}
            alt={data.camp_name} data-testid="display-logo"
            className="absolute left-[3vw] top-[3vh] h-auto max-h-[17vh] w-auto object-contain object-left drop-shadow-lg"
            style={{ maxWidth: "30vw" }} onError={() => setLogoFailed(true)} />
        )}
        {(!data.logo_file_id || logoFailed) && (
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.35em]" style={{ color: c.soft }}
            data-testid="display-camp">{data.camp_name}</p>
        )}
        <p key={`w${idx}`} className="display-fade font-display font-black leading-none tracking-tight"
          style={{ fontSize: "min(9vw, 17vh)" }} data-testid="display-welcome">
          {WELCOME[idx]}
        </p>
      </div>

      {full ? (
        <div className="flex h-2/3 items-center justify-center px-[3vw] pb-[6vh] text-center" data-testid="display-full">
          <p key={`f${idx}`} className="display-fade font-display font-black uppercase leading-[0.9] tracking-tight"
            style={{ fontSize: "min(13vw, 28vh)" }}>{FULL[idx]}</p>
        </div>
      ) : (
        <div className="grid h-2/3 grid-cols-1 items-center gap-1 px-[3vw] pb-[6vh] text-center md:grid-cols-2 md:gap-[3vw] md:text-left">
          <span className="font-display font-black leading-[0.8] tabular-nums tracking-tighter md:justify-self-end"
            style={{ fontSize: "min(24vw, 46vh)" }} data-testid="display-free">{data.free}</span>
          <span key={`p${idx}`} className="display-fade font-display font-bold leading-[0.95] md:justify-self-start"
            style={{ fontSize: "min(7vw, 13vh)" }} data-testid="display-label">
            {FREE_PITCHES[idx]}
          </span>
        </div>
      )}

      {(data.temperature !== undefined || data.hours_today) && (
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-4 px-[3vw] py-[1.2vh]"
          style={{ backgroundColor: "rgba(0,0,0,0.18)" }} data-testid="display-bar">
          <span className="flex items-center gap-[1.2vw] font-display font-bold tabular-nums"
            style={{ fontSize: "clamp(1rem, 3vw, 3rem)" }} data-testid="display-temp">
            {data.temperature !== undefined && (
              <>
                {React.createElement(weatherIcon(data.weather_code), { style: { width: "1.2em", height: "1.2em" } })}
                {data.temperature}°C
              </>
            )}
          </span>
          {data.hours_today && (
            <span key={`h${idx}`} className="display-fade flex items-center gap-[1vw] font-display font-bold"
              style={{ fontSize: "clamp(0.9rem, 2.4vw, 2.4rem)" }} data-testid="display-hours">
              <Clock style={{ width: "1.1em", height: "1.1em" }} />
              {RECEPTION[idx]} {data.hours_today === "closed" ? CLOSED[idx] : data.hours_today}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
